import { getCategoryColor, truncateLabel } from "../utils";
/**
 * Validates if data is an array
 */
export function isValidArrayData(data) {
    return data != null && Array.isArray(data);
}
/**
 * Validates if an item has the expected kpis structure
 */
export function isValidGroupedItem(item) {
    return item != null && item.kpis != null && Array.isArray(item.kpis);
}
/**
 * Generic transformer for simple/area data (single category, multiple KPIs)
 */
export function transformSimpleData(data, config, categoryLabel = "Area") {
    if (!isValidArrayData(data) || data.length === 0) {
        return { labels: [], datasets: [] };
    }
    if (!config.getKpiName || !config.getKpiValue) {
        throw new Error("transformSimpleData requires getKpiName and getKpiValue in config");
    }
    const labels = [categoryLabel];
    const datasets = data.map((item, index) => ({
        label: config.getKpiName(item),
        data: [config.getKpiValue(item)],
        backgroundColor: getCategoryColor(index),
        borderWidth: 1,
    }));
    return { labels, datasets };
}
/**
 * Generic transformer for grouped data (multiple categories, multiple KPIs each)
 */
export function transformGroupedData(data, config) {
    if (!isValidArrayData(data) || data.length === 0) {
        return { labels: [], datasets: [] };
    }
    if (!config.getGroupLabel || !config.getGroupKpis) {
        throw new Error("transformGroupedData requires getGroupLabel and getGroupKpis in config");
    }
    let groupedData = [...data];
    // Filter out items with all zero values if requested
    if (config.filterZeroValues) {
        groupedData = groupedData.filter((item) => {
            const kpis = config.getGroupKpis(item);
            return kpis && kpis.some((kpi) => kpi.value > 0);
        });
    }
    // Limit items if requested
    if (config.maxItems && config.maxItems > 0) {
        groupedData = groupedData.slice(0, config.maxItems);
    }
    if (groupedData.length === 0) {
        return { labels: [], datasets: [] };
    }
    // Get all unique KPI names across all groups
    const allKpiNames = new Set();
    groupedData.forEach((item) => {
        const kpis = config.getGroupKpis(item);
        if (kpis) {
            kpis.forEach((kpi) => allKpiNames.add(kpi.name));
        }
    });
    const kpiNames = Array.from(allKpiNames);
    // Create labels for X-axis (group names)
    const labels = groupedData.map((item) => {
        const label = config.getGroupLabel(item);
        return config.truncateLabelLength ? truncateLabel(label, config.truncateLabelLength) : label;
    });
    // Create datasets (one per KPI type)
    const datasets = kpiNames.map((kpiName, kpiIndex) => {
        const data = groupedData.map((item) => {
            const kpis = config.getGroupKpis(item);
            if (!kpis)
                return 0;
            const kpi = kpis.find((k) => k.name === kpiName);
            return kpi ? kpi.value : 0;
        });
        return {
            label: kpiName,
            data,
            backgroundColor: getCategoryColor(kpiIndex),
            borderWidth: 1,
        };
    });
    return { labels, datasets };
}
/**
 * Creates a transform config for standard KPI data structure
 */
export function createStandardKpiConfig(options) {
    if (options.isGrouped) {
        return {
            getGroupLabel: options.groupLabelGetter,
            getGroupKpis: (item) => {
                if (!item.kpis || !Array.isArray(item.kpis))
                    return [];
                return item.kpis.map((kpi) => ({
                    name: kpi[options.kpiNameField],
                    value: kpi[options.kpiValueField],
                }));
            },
            maxItems: options.maxItems,
            filterZeroValues: options.filterZeroValues,
            truncateLabelLength: options.truncateLabelLength,
        };
    }
    return {
        getKpiName: (item) => item[options.kpiNameField],
        getKpiValue: (item) => item[options.kpiValueField],
    };
}
